import { z } from 'zod';
import { DUPLICATE_CANDIDATE_STATUSES, type DuplicateCandidateStatus, type JobStatus, type OpportunityStage } from '../constants';
import { Pagination } from './common';

/**
 * Lead fields a user chooses between when merging two duplicate pizzerias. Composite fields move as a unit
 * so a merge never produces a half-and-half record: `address` = street, unit, city, province, postal code,
 * country, coordinates and timezone; `phone`/`website` carry their normalized match keys; `estimatedValue`
 * carries its currency.
 */
export const MERGE_FIELD_KEYS = [
  'name',
  'kind',
  'address',
  'phone',
  'website',
  'email',
  'category',
  'restaurantType',
  'pizzaFocus',
  'ownershipType',
  'locationCount',
  'openedOn',
  'operatingStatus',
  'description',
  'owner',
  'territory',
  'stage',
  'relationship',
  'estimatedValue',
  'budget',
  'purchaseTimeframe',
  'group',
] as const;
export type MergeFieldKey = (typeof MERGE_FIELD_KEYS)[number];

export const MERGE_FIELD_LABELS: Record<MergeFieldKey, string> = {
  name: 'Business name',
  kind: 'Type (pizzeria, dealer, rep)',
  address: 'Address',
  phone: 'Phone',
  website: 'Website',
  email: 'Email',
  category: 'Category',
  restaurantType: 'Restaurant type',
  pizzaFocus: 'Pizza focus',
  ownershipType: 'Ownership',
  locationCount: 'Locations',
  openedOn: 'Opened on',
  operatingStatus: 'Operating status',
  description: 'Description',
  owner: 'Salesperson',
  territory: 'Territory',
  stage: 'Pipeline stage',
  relationship: 'Relationship',
  estimatedValue: 'Estimated value',
  budget: 'Budget',
  purchaseTimeframe: 'Purchase timeframe',
  group: 'Restaurant group',
};

export const MERGE_SIDES = ['primary', 'secondary'] as const;
export type MergeSide = (typeof MERGE_SIDES)[number];

/** Which record's value to keep per field. Omitted fields use `defaultMergeChoice`. */
export const MergeFieldChoices = z.partialRecord(z.enum(MERGE_FIELD_KEYS), z.enum(MERGE_SIDES));
export type MergeFieldChoices = z.infer<typeof MergeFieldChoices>;

/** Default choice for a field: keep the primary's value unless it is empty and the other record has one. */
export function defaultMergeChoice(primaryEmpty: boolean, secondaryEmpty: boolean): MergeSide {
  return primaryEmpty && !secondaryEmpty ? 'secondary' : 'primary';
}

export const DuplicateListQuery = Pagination.extend({
  status: z.enum(DUPLICATE_CANDIDATE_STATUSES).default('pending'),
  minConfidence: z.coerce.number().int().min(0).max(100).optional(),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type DuplicateListQuery = z.infer<typeof DuplicateListQuery>;

/** POST /api/duplicates/:id/merge — `primaryId` must be one of the pair; the other record is absorbed. */
export const MergeDuplicateBody = z.object({
  primaryId: z.uuid(),
  fieldChoices: MergeFieldChoices.default({}),
});
export type MergeDuplicateBody = z.infer<typeof MergeDuplicateBody>;

/** POST /api/leads/:id/merge — merge `otherId` into lead `:id` (manually picked pair). */
export const MergeLeadsBody = z.object({
  otherId: z.uuid(),
  fieldChoices: MergeFieldChoices.default({}),
});
export type MergeLeadsBody = z.infer<typeof MergeLeadsBody>;

export type DuplicateSignalDTO = { signal: string; points: number; detail: string };

export type DuplicateBusinessSummaryDTO = {
  id: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  phone: string | null;
  website: string | null;
  email: string | null;
  ownerId: string | null;
  pipelineStageId: string;
  relationshipStatusId: string;
  score: number;
  tierKey: string;
  doNotContact: boolean;
  contactsCount: number;
  activitiesCount: number;
  openOpportunitiesCount: number;
  createdAt: string;
  /** Set once the record was merged away (or deleted). */
  deletedAt: string | null;
  mergedIntoId: string | null;
};

export type DuplicatePairDTO = {
  id: string;
  status: DuplicateCandidateStatus;
  confidence: number;
  signals: DuplicateSignalDTO[];
  detectedBy: string;
  detectedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  a: DuplicateBusinessSummaryDTO;
  b: DuplicateBusinessSummaryDTO;
  /** The record that would absorb the other by default (more history; older on ties). */
  suggestedPrimaryId: string;
  /** business_merges id when the pair was merged. */
  mergeId: string | null;
};

export type DuplicateCountsDTO = Record<DuplicateCandidateStatus, number>;

export type DuplicateListResponse = {
  items: DuplicatePairDTO[];
  total: number;
  page: number;
  pageSize: number;
  counts: DuplicateCountsDTO;
};

/** One comparable field; `value` is the raw value (ids for owner/stage/…), `display` is human-readable. */
export type MergeFieldValueDTO = { value: string | number | null; display: string | null; empty: boolean };

export type MergeFieldDTO = {
  key: MergeFieldKey;
  label: string;
  a: MergeFieldValueDTO;
  b: MergeFieldValueDTO;
  differs: boolean;
};

export type DuplicateContactDTO = {
  id: string;
  name: string;
  role: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  isDecisionMaker: boolean;
};

export type DuplicateEquipmentDTO = {
  id: string;
  equipmentType: string;
  brand: string | null;
  model: string | null;
  installYear: number | null;
  isCurrent: boolean;
};

export type DuplicateOpportunityDTO = {
  id: string;
  title: string;
  stage: OpportunityStage;
  estimatedValue: number;
  currency: string;
  expectedCloseDate: string | null;
};

export type DuplicateActivityDTO = { id: string; type: string; summary: string; occurredAt: string };

/** Everything that moves to the primary record on merge. */
export type DuplicateRelatedCountsDTO = {
  contacts: number;
  activities: number;
  notes: number;
  tasks: number;
  opportunities: number;
  emails: number;
  equipment: number;
  tags: number;
};

export type DuplicateSideDTO = DuplicateBusinessSummaryDTO & {
  counts: DuplicateRelatedCountsDTO;
  contacts: DuplicateContactDTO[];
  tags: { id: string; name: string; color: string }[];
  equipment: DuplicateEquipmentDTO[];
  interests: string[];
  opportunities: DuplicateOpportunityDTO[];
  recentActivities: DuplicateActivityDTO[];
  hasLiveSequence: boolean;
};

export type DuplicateCompareDTO = Omit<DuplicatePairDTO, 'a' | 'b'> & {
  a: DuplicateSideDTO;
  b: DuplicateSideDTO;
  fields: MergeFieldDTO[];
  /** Whether the current user may merge this pair (lead:merge + edit access to both). */
  canMerge: boolean;
};

export type MergeResultDTO = {
  mergeId: string;
  primaryId: string;
  mergedId: string;
  /** Rows re-pointed to the primary, per kind. */
  moved: Record<string, number>;
  /** Contacts with the same email as a primary contact: their details were folded in, the copy archived. */
  contactsCombined: number;
};

export type MergeSnapshotDTO = {
  business: Record<string, unknown>;
  contacts: Record<string, unknown>[];
  tags: { id: string; name: string }[];
  equipment: Record<string, unknown>[];
  interests: string[];
  customFieldValues: Record<string, unknown>[];
  sourceRecords: Record<string, unknown>[];
  moved?: Record<string, number>;
  combinedContacts?: { fromId: string; intoId: string; email: string }[];
};

export type MergeRecordDTO = {
  id: string;
  primaryBusinessId: string;
  primaryName: string;
  mergedBusinessId: string;
  mergedName: string;
  duplicateCandidateId: string | null;
  fieldChoices: Partial<Record<MergeFieldKey, MergeSide>>;
  mergedBy: { id: string; name: string } | null;
  mergedAt: string;
  snapshot: MergeSnapshotDTO;
};

export type DuplicateScanJobDTO = {
  id: number;
  status: JobStatus;
  processed: number;
  total: number;
  flagged: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  lastError: string | null;
};

export type DuplicateScanStatusDTO = { running: boolean; job: DuplicateScanJobDTO | null };
